import { Trans, useLingui } from '@lingui/react/macro';
import { animated, easings, useInView, useSpring } from '@react-spring/web';
import type { FC } from 'react';
import { useMission } from '@/account/referral/api';
import { Link } from '@/base/router';
import { Button } from '@/shared/Button';
import { Digits } from '@/shared/Digits';
import { cn } from '@/shared/react';
import bgImage from './background.png?w=570&quality=85&format=png&effort=max&img';

export const ReferralProgram: FC<{ className?: string }> = ({ className }) => {
  const { t } = useLingui();
  const mission = useMission();
  const [ref, inView] = useInView();

  const img = useSpring({
    filter: `blur(${inView ? 0 : 20}px)`,
    transform: `scale(${inView ? 1 : 0.8})`,
    delay: 0,
    config: { easing: easings.easeInBounce },
  });

  const btn = useSpring({
    transform: `translateY(${inView ? 0 : 25}%)`,
    delay: 0,
    config: { easing: easings.easeInBounce },
  });

  return (
    <section id="referral-program" className={cn('relative', className)}>
      <div className="px-3 mx-auto max-w-desktop gap-3 flex flex-col desktop:grid grid-cols-5 items-center">
        <div className="col-span-3 text-center desktop:text-start desktop:py-32">
          <p className="text-base text-content-secondary mb-3">
            {t`Your network is your reward engine.`}
          </p>
          <h2 className="text-2xl desktop:text-4xl leading-tight mb-3 desktop:mb-10 max-w-lg">
            {t`Refer Friends, Earn Cash, Trade for Free`}
          </h2>
          <div
            className={cn(
              'relative flex flex-col text-start rounded-lg desktop:flex-row align-middle gap-3 justify-between max-w-full w-full desktop:w-max mb-3 desktop:mb-12 h-max',
              mission.isLoading && 'shimmer !w-full',
            )}
          >
            {mission.data?.steps.map((step, i) => (
              <div
                key={`${step.invites_required}-${i}`}
                className="text-content-tertiary border border-border-disabled rounded-lg desktop:w-56 size-full p-3 shrink h-auto"
              >
                <p className="text-sm">
                  <Trans>
                    Invite{' '}
                    <Digits
                      value={step.invites_required}
                      className="font-semibold text-content-primary"
                    />{' '}
                    Friends
                  </Trans>
                </p>
                <p className="text-content-link text-lg font-bold mb-3 whitespace-normal">
                  <Trans>
                    Earn <Digits value={step.reward} coin="IQD" />
                  </Trans>
                </p>
                <p className="text-content-secondary text-xs">
                  <Trans>
                    <Digits value={step.months_no_commission} /> Months
                    Commission Free
                  </Trans>
                </p>
              </div>
            ))}
          </div>
          <animated.img
            src={bgImage}
            alt="Referral Program"
            height={350}
            className="object-contain desktop:hidden w-full mb-widget"
            style={img}
          />
          <div ref={ref}>
            <animated.div style={btn}>
              <Link to="/account/referral" className="contents">
                <Button
                  size={12}
                  variant="brand"
                  className="min-w-44 desktop:w-auto w-full"
                >
                  {t`Join Now`}
                </Button>
              </Link>
            </animated.div>
          </div>
        </div>
        <div className="col-span-2 desktop:h-full hidden desktop:block">
          <animated.img
            src={bgImage}
            alt="Referral Program"
            height={650}
            className="object-contain size-full"
            style={img}
          />
        </div>
      </div>
    </section>
  );
};
